"use client";

import { useState, useEffect } from "react";
import { cn } from "../../lib/utils";

interface RotatingTextProps {
	text: string[];
	duration?: number;
	className?: string;
	transition?: {
		duration?: number;
		ease?: string;
	};
}

export function RotatingText({
	text,
	duration = 2000,
	className,
	transition = { duration: 0.5, ease: "ease-in-out" },
}: RotatingTextProps) {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isAnimating, setIsAnimating] = useState(false);

	useEffect(() => {
		if (text.length === 0 || text.length === 1) return;

		const transitionDurationMs = (transition.duration || 0.5) * 1000;
		const cycleTime = duration + transitionDurationMs;

		let timeoutId: NodeJS.Timeout;
		let intervalId: NodeJS.Timeout | undefined;

		const triggerTransition = () => {
			setIsAnimating(true);
			setTimeout(() => {
				setCurrentIndex((prev) => (prev + 1) % text.length);
				setIsAnimating(false);
			}, transitionDurationMs);
		};

		timeoutId = setTimeout(() => {
			triggerTransition();

			setTimeout(() => {
				intervalId = setInterval(() => {
					triggerTransition();
				}, cycleTime);
			}, transitionDurationMs);
		}, duration);

		return () => {
			clearTimeout(timeoutId);
			if (intervalId) {
				clearInterval(intervalId);
			}
		};
	}, [text.length, duration, transition.duration]);

	if (text.length === 0) return null;
	if (text.length === 1) return <span className={className}>{text[0]}</span>;

	const transitionDuration = transition.duration || 0.5;
	const transitionEase = transition.ease || "ease-in-out";

	return (
		<span
			className={cn("inline-block relative overflow-hidden", className)}
			style={{
				minHeight: "1.2em",
			}}
		>
			<span
				className="inline-block transition-all"
				style={{
					transform: isAnimating ? `translateY(-100%)` : `translateY(0%)`,
					opacity: isAnimating ? 0 : 1,
					transitionDuration: `${transitionDuration}s`,
					transitionTimingFunction: transitionEase,
				}}
			>
				{text[currentIndex]}
			</span>
		</span>
	);
}
